import { makeAutoObservable } from 'mobx';
import { OptionModel, Option } from '../../../model/professional/option-model';
import toastStore from '../../../store/toast-store';
import optionStore from '../../../store/option-store';
import serviceViewModel from './service-view-model';

/**
 * Ce ViewModel sert d'intermédiaire entre les composants React (View) et le modèle de données (Model).
 * Il encapsule la logique métier liée aux options et gère les interactions avec le store MobX.
 * 
 * Responsabilités :
 * - Récupérer, créer, mettre à jour et supprimer des options
 * - Gérer les états de chargement et d'erreur
 * - Coordonner les interactions entre les différents stores
 * - Exposer des méthodes pour les composants UI
 * - Gérer les interactions avec l'interface utilisateur (modaux, dialogues)
 */

class OptionViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  private optionModel: OptionModel;

  
  setLoading(value: boolean): void {
    this.isLoading = value;
  }
  
  setError(value: string | null): void {
    this.error = value;
  }

  private setLoadingStates(isLoading: boolean): void {
    this.setLoading(isLoading);
    optionStore.setLoading(isLoading);
    if (!isLoading) return; 
    this.setError(null);
    optionStore.setError(null);
  }
  
  private handleError(message: string): void {
    console.error(message);
    this.setError(message);
    optionStore.setError(message);
  }
  
  constructor() {
    makeAutoObservable(this);
    this.optionModel = OptionModel.getInstance();
    this.setLoadingStates(false);
  }

  /**
   * Récupère toutes les options d'une catégorie spécifique
   * 
   * Flux de données :
   * 1. Met à jour les états de chargement dans le ViewModel et le store
   * 2. Enregistre la catégorie sélectionnée dans le store pour le contexte
   * 3. Appelle le modèle pour récupérer les données depuis l'API
   * 4. Valide la structure de la réponse pour assurer la robustesse
   * 5. Met à jour le store central avec les options récupérées
   * 
   * @param categoryId ID de la catégorie dont on veut récupérer les options
   * @returns Un tableau d'options ou un tableau vide en cas d'erreur
   */
  async getOptions(categoryId: string): Promise<Option[]> {
    this.setLoadingStates(true);
    
    try {
      const response = await this.optionModel.getOptions(categoryId);
      
      if (!response?.success || !Array.isArray(response.data)) {
        throw new Error(response?.message || 'Format de réponse invalide');
      }
      
      // Filtrer les options existantes pour cette catégorie
      const currentOptions = optionStore.options.filter(option => option.categoryId !== categoryId);
      
      // Ajouter les nouvelles options à la liste filtrée
      const updatedOptions = [...currentOptions, ...response.data];
      
      // Mettre à jour le store avec la liste combinée
      optionStore.setOptions(updatedOptions);
      
      return response.data;
    } catch (error: any) {
      const errorMessage = error.message || `Impossible de récupérer les options pour la catégorie ${categoryId}`;
      this.handleError(errorMessage);
      return [];
    } finally {
      this.setLoadingStates(false);
    }
  }

  async getOptionById(optionId: string): Promise<Option> {
    this.setLoadingStates(true);
    
    try {
      const existingOption = optionStore.getOptionById(optionId);
      if (existingOption) {
        this.setLoadingStates(false);
        return existingOption;
      }
  
      const response = await this.optionModel.getOptionById(optionId);
      
      if (!response?.success || !response.data) {
        throw new Error(response?.message || 'Option non trouvée');
      }
  
      optionStore.addOption(response.data);
      return response.data;
    } catch (error: any) {
      this.handleError(error.message || `Impossible de récupérer l'option ${optionId}`);
      throw error;
    } finally {
      this.setLoadingStates(false);
    }
  }

  /**
   * Supprime une option par son ID
   * 
   * Gère la suppression côté serveur et la mise à jour du store
   * pour maintenir la cohérence des données
   */
  async deleteOption(optionId: string): Promise<boolean> {
    this.setLoadingStates(true);
    
    try {
      const response = await this.optionModel.deleteOption(optionId);
      
      if (!response?.success) {
        throw new Error(response?.message || 'Échec de la suppression');
      }
  
      optionStore.removeOptionFromStore(optionId);
      toastStore.success(response.message);
      
      // Rafraîchir les services pour mettre à jour l'UI
      serviceViewModel.getServices();
      
      return true;
    } catch (error: any) {
      this.handleError(error.message || `Impossible de supprimer l'option ${optionId}`);
      return false;
    } finally {
      this.setLoadingStates(false);
    }
  }
  
  /**
   * Récupère toutes les options d'une catégorie spécifique depuis le store
   * 
   * Cette méthode est utilisée par les composants de vue pour accéder aux données
   * sans avoir à interagir directement avec le store
   * 
   * @param categoryId ID de la catégorie dont on veut récupérer les options
   * @returns Un tableau d'options appartenant à la catégorie spécifiée
   */
  getOptionsByCategoryId(categoryId: string): Option[] {
    return optionStore.getOptionsByCategoryId(categoryId);
  }
  
  /**
   * Récupère toutes les options associées à un service via ses catégories
   * 
   * Cette méthode est utilisée pour charger toutes les options d'un service en une seule fois
   * Elle récupère d'abord les catégories du service, puis les options de chaque catégorie
   * 
   * @param serviceId ID du service dont on veut récupérer les options
   * @returns Promise résolvant à un tableau de tableaux d'options, un par catégorie
   */
  async getOptionsByServiceId(serviceId: string): Promise<Option[][]> {
    try {
      // Récupérer les catégories du service
      const categories = serviceViewModel.getCategoriesByServiceId(serviceId);
      
      if (!categories || categories.length === 0) {
        return [];
      }
      
      // Récupérer les options pour chaque catégorie
      const optionsPromises = categories.map(category => {
        return this.getOptions(category.id);
      });
      
      // Attendre que toutes les requêtes soient terminées
      const results = await Promise.all(optionsPromises);
      
      return results;
    } catch (error) {
      console.error(`Erreur lors de la récupération des options pour le service ${serviceId}:`, error);
      return [];
    }
  }
}

export const optionViewModel = new OptionViewModel();
